import { attr, html, MapSignal, on, OnDispose, prop } from '@tempots/dom'

/**
 * First-time coachmark on the workspace: a card anchored under the
 * "Customize" link in the top bar, pointing new agencies to the setup
 * wizard. Shown only while the agency's design is still the onboarding
 * seed (see isUnconfiguredDesign) and retired forever on dismissal or on
 * the first visit to /customize.
 *
 * The nav link only exists at md+ widths; on smaller screens (link inside
 * the hamburger menu) the nudge simply doesn't render.
 */
export function CustomizeNudge({ onDismiss }: { onDismiss: () => void }) {
  // The Customize link's viewport rect, re-measured on resize. Null while
  // the link isn't visible (mobile nav).
  const anchor = prop<{ left: number; bottom: number; width: number } | null>(
    null
  )

  const measure = () => {
    const link = document.querySelector<HTMLElement>(
      'nav a[href$="/customize"]'
    )
    if (!link || link.offsetParent === null) {
      anchor.set(null)
      return
    }
    const r = link.getBoundingClientRect()
    anchor.set({ left: r.left, bottom: r.bottom, width: r.width })
  }
  // The top bar doesn't scroll, but fonts/images loading right after mount
  // shift the nav — re-measure on a few delayed ticks, then only on resize.
  requestAnimationFrame(measure)
  const timers = [250, 700, 1500].map(ms => setTimeout(measure, ms))
  window.addEventListener('resize', measure)
  window.addEventListener('load', measure)

  return html.div(
    OnDispose(() => {
      timers.forEach(clearTimeout)
      window.removeEventListener('resize', measure)
      window.removeEventListener('load', measure)
    }),
    MapSignal(anchor, a =>
      a === null
        ? html.span()
        : html.div(
            // Ring pulsing around the nav link itself
            html.div(
              attr.class('customize-nudge-ring'),
              attr.style(
                `position:fixed; left:${a.left - 5}px; top:${a.bottom - 37}px; width:${a.width + 10}px; height:40px; border:2px solid #0d9488; border-radius:10px; pointer-events:none; z-index:60; animation: nudge-pulse 1.6s ease infinite;`
              )
            ),
            // Card below the link, arrow pointing up at it
            html.div(
              attr.style(
                `position:fixed; left:${Math.max(8, a.left - 40)}px; top:${a.bottom + 12}px; z-index:60; max-width:330px;` +
                  'background:white; border:1px solid #99f6e4; border-radius:10px; box-shadow:0 10px 30px rgba(0,0,0,0.18); padding:14px 16px;'
              ),
              html.div(
                attr.style(
                  `position:absolute; top:-6px; left:${a.left - Math.max(8, a.left - 40) + a.width / 2 - 6}px; width:12px; height:12px; background:white; border-left:1px solid #99f6e4; border-top:1px solid #99f6e4; transform:rotate(45deg);`
                )
              ),
              html.button(
                attr.type('button'),
                attr.style(
                  'position:absolute; top:6px; right:8px; border:none; background:none; color:#9ca3af; font-size:16px; cursor:pointer; line-height:1;'
                ),
                attr.title('Dismiss'),
                on.click(onDismiss),
                '×'
              ),
              html.div(
                attr.style(
                  'font-weight:700; color:#134e4a; font-size:15px; margin-bottom:4px;'
                ),
                'Start here: set up your header'
              ),
              html.div(
                attr.style(
                  'color:#374151; font-size:14px; line-height:1.5; padding-right:8px;'
                ),
                'Your resumes are using a starter design. Head to ',
                html.b('Customize'),
                ' to copy your existing header or build your own — it takes a couple of minutes.'
              )
            ),
            // Keyframes for the pulse (scoped, injected once per mount)
            html.style(
              '@keyframes nudge-pulse { 0%,100% { box-shadow:0 0 0 0 rgba(13,148,136,0.35);} 50% { box-shadow:0 0 0 7px rgba(13,148,136,0);} }'
            )
          )
    )
  )
}
